import {
  IPubmaticBidRequest,
  IIXVideoBidRequest,
  IOneVideoBidRequest,
  IS2SBidRequest,
  IRoundelVideoBidRequest,
  IGumGumBidRequest,
  IAmazonBidRequest
} from '../../../typings/IPrebid'
import {
  DEFAULT_MAX_DURATION,
  DEFAULT_MIN_DURATION,
  DEFAULT_VIDEO_AD_HEIGHT,
  DEFAULT_VIDEO_AD_WIDTH,
  TEST_BID_FLOOR,
  DEFAULT_OUTSTREAM_MIN_DURATION,
  DEFAULT_OUTSTREAM_MAX_DURATION
} from '../../constants/video'
import {
  IndexExchangeVideo,
  OneVideo,
  Pubmatic,
  Gumgum,
  Amazon
} from '../../enums/VideoBidder'
import {
  API,
  DeliveryMethod,
  Linearity,
  Placement,
  PlaybackMethod,
  Protocol
} from '../../enums/OpenRTB'
import { Bidder } from '../../enums/index'
import { PENNY_PRECISION } from '../../constants/index'
import { VideoBidder, OutstreamBidder } from '../../enums/Bidder'
import {
  IVideoAdTagOptions,
  IVideoBidRestrictions
} from '../../../typings/IVideoOptions'
import { Flooring } from '../../flooring/Flooring'
import { ITestBids } from './VideoPrebidManager'
import { DiscrepancyValues } from '../BidderSettings'
import { LiftTest } from '../../liftTest/LiftTest'
import { queryParamHas } from '../../helpers/getQueryParam'
import WebModel from '../../models/WebModel'

export default class VideoBidRequest {
  public readonly options: IVideoAdTagOptions
  private readonly webModel: WebModel | IWebViewModel
  public readonly overrides: any
  private playerHeight: number
  private playerWidth: number
  private floor: number
  private testBids: ITestBids
  private isOutstream: boolean
  private minDuration: number
  private maxDuration: number
  private skippable: boolean
  private discrepancies: DiscrepancyValues

  constructor(
    options: IVideoAdTagOptions,
    webmodel: WebModel | IWebViewModel,
    overrides: any = {},
    testBids: ITestBids
  ) {
    this.options = options
    this.overrides = overrides
    this.webModel = webmodel
    this.playerHeight = this.options.contentHeight || DEFAULT_VIDEO_AD_HEIGHT
    this.playerWidth = this.options.contentWidth || DEFAULT_VIDEO_AD_WIDTH
    this.testBids = testBids || {}
    this.isOutstream = this.options.placement === Placement.article

    // A bunch of values depend on whether this is for an Outstream or Instream bid:
    this.floor = this.isOutstream
      ? Flooring.getOutstreamFloor(webmodel)
      : Flooring.getVideoFloor(webmodel)
    this.minDuration = this.isOutstream
      ? DEFAULT_OUTSTREAM_MIN_DURATION
      : DEFAULT_MIN_DURATION
    this.maxDuration = this.isOutstream
      ? DEFAULT_OUTSTREAM_MAX_DURATION
      : DEFAULT_MAX_DURATION
    this.discrepancies = this.isOutstream
      ? this.webModel.outstreamDiscrepancies
      : this.webModel.videoDiscrepancies
    this.skippable = this.isOutstream ? false : true
  }

  // Uses discrepancies and gross bid adjustments to calculate the bidFloor for a particular bidder.
  private calcFloor(
    bidderCode: VideoBidder | OutstreamBidder,
    grossAdjustment = 1
  ): number {
    const discrepancies = this.discrepancies || {}
    const discrepancy = discrepancies[bidderCode] || 1
    const oneCent = 0.01

    const bidFloor = roundNumberToPrecision(
      this.floor / grossAdjustment / discrepancy + oneCent,
      PENNY_PRECISION
    )

    return bidFloor
  }

  get amazon(): IAmazonBidRequest {
    const bidFloor = this.calcFloor(VideoBidder.uam)

    let slotId

    if (this.testBids.uam) {
      // siteId = Amazon.testSiteId
    } else if (this.options.mobile) {
      slotId = this.options.autoplay
        ? Amazon.siteIdAutoplayMobile
        : Amazon.siteIdCTPMobile
    } else {
      slotId = this.options.autoplay
        ? Amazon.siteIdAutoplayDesktop
        : Amazon.siteIdCTPDesktop
    }

    return {
      bidder: VideoBidder.uam,
      params: {
        bidfloor: bidFloor,
        slotId: slotId
      }
    }
  }

  get gumgum(): IGumGumBidRequest {
    let zoneId

    if (this.testBids.gumgum) {
      zoneId = Gumgum.testId
    } else {
      zoneId = this.webModel.gumgum_id
    }

    const bidFloor = this.calcFloor(VideoBidder.gumgum)

    return {
      bidder: VideoBidder.gumgum,
      params: {
        bidfloor: bidFloor,
        video: zoneId
      }
    }
  }

  getIndexExchangeBidRequest(
    bidSpecificOptions: IVideoBidRestrictions
  ): IIXVideoBidRequest {
    const test = this.testBids.indexExchange
    let siteId

    if (test) {
      siteId = IndexExchangeVideo.testSiteId
    } else if (this.options.mobile) {
      if (this.isOutstream) {
        siteId = IndexExchangeVideo.siteIdOutstreamMobile
      } else {
        siteId = this.options.autoplay
          ? IndexExchangeVideo.siteIdAutoplayMobile
          : IndexExchangeVideo.siteIdCTPMobile
      }
    } else {
      if (this.isOutstream) {
        siteId = IndexExchangeVideo.siteIdOutstreamDesktop
      } else {
        siteId = this.options.autoplay
          ? IndexExchangeVideo.siteIdAutoplayDesktop
          : IndexExchangeVideo.siteIdCTPDesktop
      }
    }

    // https://knowledgebase.indexexchange.com/display/VIDEO/Parameters
    return {
      bidder: VideoBidder.indexExchange,
      params: {
        bidFloor: this.calcFloor(VideoBidder.indexExchange),
        bidFloorCur: 'USD',
        debug: test,
        referrer: top.window.location.href,
        siteId,
        size: [this.playerWidth, this.playerHeight],
        timeout: IndexExchangeVideo.timeout,
        video: {
          api: [API.VPAID_1_0, API.VPAID_2_0],
          delivery: [DeliveryMethod.progressive],
          linearity: Linearity.linear,
          maxduration: this.maxDuration,
          minduration: this.minDuration,
          mimes: this.options.mimes,
          placement: this.options.placement || Placement.stream,
          playbackmethod: this.options.autoplay
            ? [PlaybackMethod.autoplaySoundOff]
            : [PlaybackMethod.ctp],
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          skip: this.skippable,
          startdelay: bidSpecificOptions.startDelay || 0,
          w: this.playerWidth,
          h: this.playerHeight
        }
      }
    }
  }

  get roundel(): IRoundelVideoBidRequest {
    const siteId = '438180'

    // https://knowledgebase.indexexchange.com/display/VIDEO/Parameters
    return {
      bidder: VideoBidder.roundel,
      params: {
        siteId,
        size: [this.playerWidth, this.playerHeight],
        // @ts-ignore testing out before writing a interface
        video: {
          mimes: this.options.mimes,
          maxduration: this.maxDuration,
          minduration: this.minDuration,
          placement: this.options.placement || Placement.stream,
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          api: [API.VPAID_1_0, API.VPAID_2_0],
          skip: this.skippable
        },
        bidFloor: this.calcFloor(VideoBidder.indexExchange),
        bidFloorCur: 'USD'
      }
    }
  }

  get oneVideo(): IOneVideoBidRequest {
    let pubId
    let inventoryId
    let site
    // note: bidfloor is undocumented, but in their adapter code.
    // don't know if they want a string or a number.
    let bidFloor
    const oneVideoPennyAdjustment = 0.01

    if (this.testBids.oneVideo) {
      pubId = OneVideo.testPublisherId
      site = {
        id: OneVideo.testSiteId,
        page: OneVideo.testPage,
        referrer: OneVideo.testReferrer
      }
      bidFloor = TEST_BID_FLOOR
    } else if (this.isOutstream) {
      if (this.options.mobile) {
        inventoryId = OneVideo.outstreamMobile
      } else {
        inventoryId = OneVideo.outstreamDesktop
      }
      pubId = OneVideo.outstreamPublisherId
      site = {
        page: top.window.location.href
      }
      bidFloor = this.calcFloor(VideoBidder.oneVideo)
    } else {
      pubId = OneVideo.publisherId
      site = {
        page: top.window.location.href
      }
      bidFloor = this.calcFloor(VideoBidder.oneVideo)
    }

    return {
      bidder: VideoBidder.oneVideo,
      params: {
        bidfloor: bidFloor,
        pubId,
        site,
        video: {
          api: [API.VPAID_2_0],
          delivery: [DeliveryMethod.progressive],
          inventoryid: inventoryId,
          maxduration: this.maxDuration,
          minduration: this.minDuration,
          mimes: this.options.mimes,
          placement: this.options.placement || Placement.stream,
          playerHeight: this.playerHeight,
          playerWidth: this.playerWidth,
          position: 1,
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          skip: this.skippable
        }
      }
    }
  }

  get S2S(): IS2SBidRequest {
    const liftTest = LiftTest.new(this.webModel)
    const liftTestBidder = liftTest.bidder
    const liftTestMediaType = liftTest.mediaType
    return {
      bidder: Bidder.S2S,
      params: {
        floor: this.floor,
        autoplay: this.options.autoplay,
        mobile: this.options.mobile,
        desktop: !this.options.mobile,
        placement: this.isOutstream ? Placement.article : Placement.stream,
        skip: this.isOutstream ? 0 : 1,
        liftTestBidder,
        liftTestMediaType
      }
    }
  }

  getPubmaticBidRequest(
    bidSpecificOptions: IVideoBidRestrictions
  ): IPubmaticBidRequest {
    const wh = `${this.playerWidth}x${this.playerHeight}`
    let publisherId = Pubmatic.publisherId
    let adSlot

    // We want to be able to force an outstream ad, and Pubmatic makes it easy.
    const forceOutstream =
      this.isOutstream && queryParamHas('test', 'outstream')

    if (this.testBids.pubmatic || forceOutstream) {
      publisherId = Pubmatic.testPublisherId
      adSlot = `${Pubmatic.testAdSlot}@${wh}`
    } else if (this.isOutstream) {
      if (this.options.mobile) {
        adSlot = `${Pubmatic.adSlotOutstreamMobile}@${wh}`
      } else {
        adSlot = `${Pubmatic.adSlotOutstreamDesktop}@${wh}`
      }
    } else if (this.isMobileAutoplay()) {
      adSlot = `${Pubmatic.adSlotAdhesion}@${wh}`
    } else if (
      (!this.options.mobile && this.options.sticky) ||
      this.isDesktopAutoplay()
    ) {
      adSlot = `${Pubmatic.adSlotSticky}@${wh}`
    } else if (this.options.mobile) {
      adSlot = `${Pubmatic.adSlotMobile}@${wh}`
    } else {
      adSlot = `${Pubmatic.adSlotDesktop}@${wh}`
    }

    return {
      bidder: VideoBidder.pubmatic,
      params: {
        adSlot,
        kadfloor: this.calcFloor(VideoBidder.pubmatic).toString(),
        bcat: this.webModel.iabOptouts,
        outstreamAU: 'no thank you',
        publisherId,
        video: {
          api: [API.VPAID_2_0],
          delivery: [DeliveryMethod.progressive],
          linearity: Linearity.linear,
          minduration: this.minDuration,
          maxduration: this.maxDuration,
          mimes: this.options.mimes,
          placement: this.options.placement || Placement.stream,
          playbackmethod: this.options.autoplay
            ? [PlaybackMethod.autoplaySoundOff]
            : [PlaybackMethod.ctp],
          protocols: [
            Protocol.VAST_2_0,
            Protocol.VAST_3_0,
            Protocol.VAST_2_0_wrapper,
            Protocol.VAST_3_0_wrapper,
            Protocol.VAST_4_0,
            Protocol.VAST_4_0_wrapper
          ],
          skippable: this.skippable,
          skip: this.skippable,
          startdelay: bidSpecificOptions.startDelay || 0
        }
      }
    }
  }

  private isMobileAutoplay(): boolean {
    return this.options.mobile && !!this.options.manualOptions.isAutomobile
  }
  private isDesktopAutoplay(): boolean {
    return !this.options.mobile && !!this.options.manualOptions.isAutoplay
  }
}

export function roundNumberToPrecision(num: number, precision: number): number {
  const power = Math.pow(10, precision)

  return Math.floor(num * power) / power
}
